import Avatar from '@shared/components/Avatar/Avatar';
import Checkbox from '@shared/components/Checkbox/Checkbox';
import { KnownMembership } from 'matrix-js-sdk/src/matrix';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CenteredSpinner from 'components/CenteredSpinner/CenteredSpinner';
import NoResultsPlaceholder from 'components/NoResultsPlaceholder/NoResultsPlaceholder';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { buildSuggestionsFromRooms } from 'modules/Messenger/components/InviteMembersField/suggestedUsersScoring';

import { memberMatchesInviteSearch } from './memberMatchesInviteSearch';

// Cap how many suggestions render at once; the scoring already returns them best-first.
const MAX_VISIBLE_SUGGESTED_MEMBERS = 50;

interface Candidate {
  userId: string;
  displayName: string;
}

interface SuggestedMembersListProps {
  searchTerm: string;
  participants: string[];
  onToggle: (mxid: string, displayName?: string) => void;
  excludeUserIds?: ReadonlySet<string>;
}

const SuggestedMembersList = ({ searchTerm, participants, onToggle, excludeUserIds }: SuggestedMembersListProps) => {
  const { t } = useTranslation();
  const { matrixClient } = useMatrixContext();
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Recompute the scored suggestions every time the list is shown (fresh snapshot of room state).
  // The scoring is synchronous and can be heavy, so defer it one tick to let the spinner paint
  // before it blocks the main thread.
  useEffect(() => {
    setIsLoading(true);
    const handle = window.setTimeout(() => {
      const currentUserId = matrixClient.getUserId();
      const rooms = matrixClient.getRooms().filter((r) => r.getMyMembership() === KnownMembership.Join);
      const excluded = [...(currentUserId ? [currentUserId] : []), ...(excludeUserIds ? [...excludeUserIds] : [])];
      const next = buildSuggestionsFromRooms(rooms, excluded)
        .slice(0, MAX_VISIBLE_SUGGESTED_MEMBERS)
        .map((m) => ({ userId: m.userId, displayName: m.name || m.userId }));
      setCandidates(next);
      setIsLoading(false);
    }, 0);

    return () => window.clearTimeout(handle);
  }, [matrixClient, excludeUserIds]);

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return candidates;
    return candidates.filter((c) => memberMatchesInviteSearch(c.displayName, c.userId, searchTerm));
  }, [candidates, searchTerm]);

  if (isLoading) {
    return <CenteredSpinner className="!mb-0 py-4" />;
  }

  if (filtered.length === 0) {
    return <NoResultsPlaceholder className="bg-white !mb-0 !p-2" title={t('workspaces.no_results')} />;
  }

  return (
    <div className="flex min-w-0 flex-col">
      {filtered.map((c) => (
        <div key={c.userId} className="flex items-center justify-between py-1">
          <div className="flex min-w-0 items-center gap-4">
            <Avatar size="xs" name={c.displayName} />
            <span className="min-w-0 truncate text-size-sm font-medium">{c.displayName}</span>
          </div>
          <Checkbox checked={participants.includes(c.userId)} onChange={() => onToggle(c.userId, c.displayName)} />
        </div>
      ))}
    </div>
  );
};

export default SuggestedMembersList;
